import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { Spinner } from '@nextui-org/react';
import { AlertCircle, ImageOff } from 'lucide-react';
import dynamicPageService, {
  ContentBlock,
  DynamicPage as DynamicPageModel,
} from '../services/dynamicPageService';

const ACCENT = '#3fced1';

/**
 * Dynamische CMS-Seite unter /dartcloud/:slug (z. B. Impressum, Datenschutz).
 * Port der Flutter DynamicPageView – rendert Content-Blocks.
 */
const DynamicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<DynamicPageModel | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPage(null);
    if (!slug) {
      setLoading(false);
      return;
    }
    dynamicPageService
      .getPageBySlug(slug)
      .then((p) => {
        if (!cancelled) setPage(p);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-zinc-950">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!page || page.isVisible === false) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 bg-zinc-950 px-4 text-center">
        <AlertCircle size={56} className="text-zinc-700" />
        <h1 className="text-2xl font-bold text-white">Seite nicht gefunden</h1>
        <p className="text-gray-500">Die Seite „/dartcloud/{slug}" existiert nicht.</p>
      </div>
    );
  }

  const lastUpdated =
    page.showLastUpdated && page.updatedAt
      ? new Date(page.updatedAt).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })
      : null;

  const blocks = [...page.contentBlocks].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen bg-zinc-950">
      <Helmet>
        <title>{page.title ? `${page.title} | DartCloud` : 'DartCloud'}</title>
        {page.metaDescription && <meta name="description" content={page.metaDescription} />}
      </Helmet>

      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-6 sm:py-14">
        <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
          {page.title}
        </h1>

        {lastUpdated && (
          <p className="mt-3 text-sm text-gray-500">Zuletzt aktualisiert: {lastUpdated}</p>
        )}

        <div className="mt-8">
          {blocks.map((block, i) => (
            <BlockRenderer key={block.id || i} block={block} />
          ))}
        </div>
      </div>
    </div>
  );
};

const BlockRenderer: React.FC<{ block: ContentBlock }> = ({ block }) => {
  switch (block.type) {
    case 'heading': {
      const cls =
        block.level === 1
          ? 'text-2xl font-extrabold sm:text-[28px]'
          : block.level === 3
          ? 'text-lg font-semibold sm:text-xl'
          : 'text-xl font-bold sm:text-2xl';
      return <h2 className={`mb-3 mt-6 leading-snug tracking-tight text-white ${cls}`}>{block.text}</h2>;
    }
    case 'paragraph':
      return (
        <p className="mb-4 whitespace-pre-wrap text-[16px] leading-relaxed text-gray-300">
          {block.text}
        </p>
      );
    case 'list':
      if (!block.listItems.length) return null;
      return (
        <ul className="mb-4 space-y-2 pl-2">
          {block.listItems.map((item, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="w-5 shrink-0 font-semibold" style={{ color: ACCENT }}>
                {block.listType === 'numbered' ? `${idx + 1}.` : '•'}
              </span>
              <span className="text-[16px] leading-relaxed text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'image':
      return <BlockImage block={block} />;
    case 'divider':
      return <hr className="my-6 border-zinc-800" />;
    default:
      return null;
  }
};

const BlockImage: React.FC<{ block: ContentBlock }> = ({ block }) => {
  const [error, setError] = useState(false);
  const src = dynamicPageService.buildImageUrl(block.imageUrl);
  if (!src) return null;
  const maxWidth = block.imageMaxWidth && block.imageMaxWidth > 0 ? block.imageMaxWidth : undefined;

  return (
    <figure className="my-6" style={maxWidth ? { maxWidth } : undefined}>
      {error ? (
        <div className="flex h-48 w-full items-center justify-center rounded-xl bg-zinc-800">
          <ImageOff size={44} className="text-zinc-600" />
        </div>
      ) : (
        <img
          src={src}
          alt={block.imageAlt || ''}
          onError={() => setError(true)}
          className="w-full rounded-xl object-cover"
        />
      )}
      {block.imageCaption && (
        <figcaption className="mt-2 text-sm italic text-gray-500">{block.imageCaption}</figcaption>
      )}
    </figure>
  );
};

export default DynamicPage;
